import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'antd';

import { themeToggleValue } from '~/common/utils/theme';
import { defaultTheme } from '~/themes/themes';
import { TableNameTypes, useTracking } from '~/hooks/useTracking';

type OutstandingBalanceValueProps = {
  value?: string;
  rawValue?: number | string;
  userId?: number | string;
  personId?: number | string;
  agreementName?: string;
  contractId?: number | string;
};

const ValueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ValueText = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => themeToggleValue(theme, '#000000', { dark: theme.colors.element.secondary })};
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme?.primaryColor || defaultTheme.primaryColor};

  &:active {
    opacity: 0.6;
  }
`;

export default function OutstandingBalanceValue({
  value,
  rawValue,
  userId,
  personId,
  agreementName,
  contractId,
}: OutstandingBalanceValueProps) {
  const { handleTracking } = useTracking();
  const [isHidden, setIsHidden] = useState(true);
  const hasValue = !!value && value !== '-';
  const hiddenValue = useMemo(() => {
    if (!hasValue) return 'R$ *';
    const source = rawValue !== undefined && rawValue !== null ? String(rawValue) : value || '';
    const digitsCount = source.replace(/[^0-9,.]/g, '').length || 1;
    return `R$ ${'*'.repeat(digitsCount)}`;
  }, [hasValue, rawValue, value]);
  let displayValue: string;
  if (!hasValue) {
    displayValue = '-';
  } else if (isHidden) {
    displayValue = hiddenValue;
  } else {
    displayValue = value || '-';
  }
  const minWidth = useMemo(() => {
    if (!hasValue) return undefined;
    const visibleLength = value?.length || 0;
    const hiddenLength = hiddenValue.length;
    const maxLength = Math.max(visibleLength, hiddenLength, 1);
    const adjustedLength = Math.max(maxLength - 1, 1);
    return `${adjustedLength}ch`;
  }, [hasValue, value, hiddenValue, isHidden]);

  const handleToggle = () => {
    setIsHidden((prev) => {
      const next = !prev;
      if (prev && !next) {
        const event = {
          event_name: 'visualizou_valor_quitacao',
          cliente_user_id: userId ?? null,
          titulo_tela: 'Contrato',
          cliente_person_id: personId ?? null,
          cliente_convenio: agreementName ?? null,
          contrato_id: contractId ?? null,
          valor_visualizado: value ?? null,
          cliente_oportunidade: null,
          cliente_contrato_pago: null,
        };

        handleTracking(event, TableNameTypes.BACKOFFICE_VIEW);
      }
      return next;
    });
  };

  return (
    <ValueWrapper>
      <ValueText style={minWidth ? { minWidth } : undefined}>{displayValue}</ValueText>
      {hasValue && (
        <ToggleButton type="button" onClick={handleToggle} aria-label={isHidden ? 'Mostrar valor' : 'Ocultar valor'}>
          <Icon type={isHidden ? 'eye' : 'eye-invisible'} />
        </ToggleButton>
      )}
    </ValueWrapper>
  );
}
